import React, { useState } from 'react'
import { NotesTable } from 'components/Tables/NotesTable/NotesTable'
import NotesFilterset from 'components/Tables/NotesTable/NotesFilterset'
import { FilterIdentifier } from 'models/saved_view'
import { Tabs } from 'components/FbUI/Tabs'
import { CompanyModal } from '../CompanyModal'
import { formatInteger } from 'utils/formatting'
import apiService from 'services/api'

interface NotesListModalProps {
  show: boolean
  onHide: () => void
  chainId?: number
  contactCompanyId?: number
  doorProxyId?: number
  companyName?: string
}

export function NotesListModal({
  show,
  onHide,
  chainId,
  contactCompanyId,
  doorProxyId,
  companyName,
}: NotesListModalProps) {
  const [companyNotesCount, setCompanyNotesCount] = useState(0)
  const [doorNotesCount, setDoorNotesCount] = useState(0)
  const [selectedTab, setSelectedTab] = useState<'company' | 'door'>('company')

  const api = apiService()
  const { data: chainData } = chainId
    ? api.useGetChain(chainId)
    : { data: undefined }

  const effectiveContactCompanyId =
    contactCompanyId || chainData?.contact_company_id

  const isCompanyPage = !doorProxyId && (chainId || contactCompanyId)

  const COMPANY_NOTES_FILTER_IDENTIFIER =
    'COMPANY_NOTES_TABLE' as FilterIdentifier
  const DOOR_NOTES_FILTER_IDENTIFIER = 'DOOR_NOTES_TABLE' as FilterIdentifier
  const NOTES_TABLE_FILTER_IDENTIFIER = 'NOTES_TABLE' as FilterIdentifier

  const COMPANY_TABLE_KEY = `notes-modal-company-${contactCompanyId || chainId}-${COMPANY_NOTES_FILTER_IDENTIFIER}`
  const DOOR_TABLE_KEY = `notes-modal-door-${contactCompanyId || chainId}-${DOOR_NOTES_FILTER_IDENTIFIER}`
  const TABLE_KEY = `notes-modal-${contactCompanyId || chainId || doorProxyId}-${NOTES_TABLE_FILTER_IDENTIFIER}`

  if (isCompanyPage) {
    return (
      <CompanyModal
        show={show}
        onHide={onHide}
        companyName={companyName}
        modalTitle="Notes"
      >
        <div className="w-full">
          <Tabs
            tabs={[
              {
                key: 'company',
                title: `Company Notes (${formatInteger(companyNotesCount)})`,
              },
              {
                key: 'door',
                title: `Door Notes (${formatInteger(doorNotesCount)})`,
              },
            ]}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />

          <div>
            <div
              className={`max-h-[580px] flex pb-1 pt-4 ${selectedTab === 'company' ? '' : 'hidden'}`}
            >
              <NotesTable
                tableKey={COMPANY_TABLE_KEY}
                filterIdentifier={COMPANY_NOTES_FILTER_IDENTIFIER}
                savedPresetsIdentifier="NOTES_TABLE"
                notesFilterset={
                  <NotesFilterset
                    filterIdentifier={COMPANY_NOTES_FILTER_IDENTIFIER}
                    showDoorFilters={false}
                    contactCompanyId={effectiveContactCompanyId}
                  />
                }
                contactCompanyId={contactCompanyId}
                chainId={chainId}
                doorProxyId={doorProxyId}
                setTotalRowsCount={setCompanyNotesCount}
                enablePagination={true}
                showDoorColumns={false}
                noteType="company"
              />
            </div>

            <div
              className={`max-h-[580px] flex pb-1 pt-4 ${selectedTab === 'door' ? '' : 'hidden'}`}
            >
              <NotesTable
                tableKey={DOOR_TABLE_KEY}
                filterIdentifier={DOOR_NOTES_FILTER_IDENTIFIER}
                defaultColumnVisibility={{
                  door_country: false,
                  door_city: false,
                  door_state: false,
                }}
                savedPresetsIdentifier="DOOR_NOTES_TABLE"
                notesFilterset={
                  <NotesFilterset
                    filterIdentifier={DOOR_NOTES_FILTER_IDENTIFIER}
                    showDoorFilters={true}
                    contactCompanyId={effectiveContactCompanyId}
                  />
                }
                contactCompanyId={contactCompanyId}
                chainId={chainId}
                doorProxyId={doorProxyId}
                setTotalRowsCount={setDoorNotesCount}
                enablePagination={true}
                showDoorColumns={true}
                noteType="door"
              />
            </div>
          </div>
        </div>
      </CompanyModal>
    )
  }

  // For door pages
  return (
    <CompanyModal
      show={show}
      onHide={onHide}
      companyName={companyName}
      modalTitle="Notes"
    >
      <NotesTable
        tableKey={TABLE_KEY}
        filterIdentifier={NOTES_TABLE_FILTER_IDENTIFIER}
        savedPresetsIdentifier="NOTES_TABLE"
        notesFilterset={
          <NotesFilterset
            filterIdentifier={NOTES_TABLE_FILTER_IDENTIFIER}
            showDoorFilters={false}
            contactCompanyId={effectiveContactCompanyId}
          />
        }
        contactCompanyId={contactCompanyId}
        chainId={chainId}
        doorProxyId={doorProxyId}
        setTotalRowsCount={setCompanyNotesCount}
        enablePagination={true}
        showDoorColumns={false}
        noteType={undefined}
      />
    </CompanyModal>
  )
}
